// ============================================
// COMPANY PROFILE PAGE
// ============================================

import { createFileRoute } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Building2, Loader2, Save, Upload, Image, CheckCircle2, Users, Trash2 } from 'lucide-react';
import { PageHeader } from '@/components';
import { useMyCompany, useUpdateCompany, useUploadLogo, useUploadBanner, useDeleteLogo, useDeleteBanner } from '@/hooks';
import { useState, useRef, useCallback, useEffect } from 'react';
import { vesselTypeLabels } from '@/utils';
import { config } from '@/config';
import { useTranslation } from '@crew-at-sea/i18n';

export const Route = createFileRoute('/_authenticated/profile/')({
  component: ProfilePage,
});

function ProfilePage() {
  const { t } = useTranslation('panel');
  const { data: companyResponse, isLoading } = useMyCompany();
  const updateMutation = useUpdateCompany();
  const uploadLogoMutation = useUploadLogo();
  const uploadBannerMutation = useUploadBanner();
  const deleteLogoMutation = useDeleteLogo();
  const deleteBannerMutation = useDeleteBanner();

  const company: any = (companyResponse as any)?.data || companyResponse;

  const profileSchema = z.object({
    name: z.string().min(2, t('profile.validation.nameRequired')),
    description: z.string().optional(),
    country: z.string().min(1, t('profile.validation.countryRequired')),
    city: z.string().optional(),
    address: z.string().optional(),
    website: z.string().url(t('profile.validation.urlInvalid')).optional().or(z.literal('')),
    contactEmail: z.string().email(t('profile.validation.emailInvalid')).optional().or(z.literal('')),
    contactPhone: z.string().optional(),
    foundedYear: z.number().min(1800).max(new Date().getFullYear()).optional(),
    employeeCount: z.string().optional(),
    fleetSize: z.number().min(0).optional(),
    vesselTypes: z.array(z.string()).optional(),
  });

  type ProfileFormData = z.infer<typeof profileSchema>;

  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [bannerPreview, setBannerPreview] = useState<string | null>(null);
  const [logoProgress, setLogoProgress] = useState(0);
  const [bannerProgress, setBannerProgress] = useState(0);
  const [selectedVesselTypes, setSelectedVesselTypes] = useState<string[]>([]);

  const logoInputRef = useRef<HTMLInputElement>(null);
  const bannerInputRef = useRef<HTMLInputElement>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
  } = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      name: '',
      description: '',
      country: '',
      city: '',
      address: '',
      website: '',
      contactEmail: '',
      contactPhone: '',
      foundedYear: undefined,
      employeeCount: '',
      fleetSize: 0,
      vesselTypes: [],
    },
  });

  // Track if vessel types changed from original
  const [vesselTypesChanged, setVesselTypesChanged] = useState(false);
  const [originalVesselTypes, setOriginalVesselTypes] = useState<string[]>([]);

  // Reset form when company data loads
  useEffect(() => {
    if (company) {
      reset({
        name: company.name || '',
        description: company.description || '',
        country: company.country || '',
        city: company.city || '',
        address: company.address || '',
        website: company.website || '',
        contactEmail: company.contactEmail || '',
        contactPhone: company.contactPhone || '',
        foundedYear: company.foundedYear || undefined,
        employeeCount: company.employeeCount || '',
        fleetSize: company.fleetSize || 0,
        vesselTypes: company.vesselTypes || [],
      });
      
      const types = Array.isArray(company.vesselTypes) ? company.vesselTypes : [];
      setSelectedVesselTypes(types);
      setOriginalVesselTypes(types);
      setVesselTypesChanged(false);
    }
  }, [company, reset]);

  // Check if form has any changes
  const hasChanges = isDirty || vesselTypesChanged;

  const onSubmit = (data: ProfileFormData) => {
    updateMutation.mutate({
      ...data,
      vesselTypes: selectedVesselTypes,
    });
  };

  const handleLogoUpload = useCallback((file: File) => {
    if (!file.type.startsWith('image/')) {
      alert(t('profile.upload.imageOnly'));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert(t('profile.upload.maxSize5'));
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    setLogoPreview(objectUrl);
    setLogoProgress(0);

    uploadLogoMutation.mutate({
      file,
      onProgress: (percent) => setLogoProgress(percent),
    }, {
      onSuccess: () => {
        setTimeout(() => {
          setLogoPreview(null);
          URL.revokeObjectURL(objectUrl);
        }, 1000);
      },
      onError: () => {
        setLogoPreview(null);
        URL.revokeObjectURL(objectUrl);
      },
    });
  }, [uploadLogoMutation, t]);

  const handleBannerUpload = useCallback((file: File) => {
    if (!file.type.startsWith('image/')) {
      alert(t('profile.upload.imageOnly'));
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      alert(t('profile.upload.maxSize10'));
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    setBannerPreview(objectUrl);
    setBannerProgress(0);

    uploadBannerMutation.mutate({
      file,
      onProgress: (percent) => setBannerProgress(percent),
    }, {
      onSuccess: () => {
        setTimeout(() => {
          setBannerPreview(null);
          URL.revokeObjectURL(objectUrl);
        }, 1000);
      },
      onError: () => {
        setBannerPreview(null);
        URL.revokeObjectURL(objectUrl);
      },
    });
  }, [uploadBannerMutation, t]);

  const handleLogoDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleLogoUpload(file);
  }, [handleLogoUpload]);

  const handleBannerDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleBannerUpload(file);
  }, [handleBannerUpload]);

  const handleDeleteLogo = useCallback(() => {
    if (window.confirm('Şirket logosunu silmek istediğinize emin misiniz?')) {
      deleteLogoMutation.mutate();
    }
  }, [deleteLogoMutation]);

  const handleDeleteBanner = useCallback(() => {
    if (window.confirm('Şirket afişini (banner) silmek istediğinize emin misiniz?')) {
      deleteBannerMutation.mutate();
    }
  }, [deleteBannerMutation]);

  const toggleVesselType = (type: string) => {
    setSelectedVesselTypes(prev => {
      const newTypes = prev.includes(type) 
        ? prev.filter(t => t !== type) 
        : [...prev, type];
      
      const changed = JSON.stringify(newTypes.sort()) !== JSON.stringify(originalVesselTypes.sort());
      setVesselTypesChanged(changed);
      
      return newTypes;
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-maritime-600 animate-spin" />
      </div>
    );
  }

  // Helper function to get full URL for uploads
  const getFullUrl = (path: string | null | undefined) => {
    if (!path) return null;
    if (path.startsWith('http') || path.startsWith('data:')) return path;
    if (path.startsWith('/uploads')) return `${config.uploadsBaseUrl}${path}`;
    return path;
  };

  const logoUrl = logoPreview || getFullUrl(company?.logoUrl);
  const bannerUrl = bannerPreview || getFullUrl(company?.bannerUrl);

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('profile.pageTitle')}
        description={t('profile.pageDescription')}
      />

      {/* Followers Stat Banner */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 rounded-maritime p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 flex-shrink-0">
            <Users className="w-5 h-5" />
          </div>
          <div>
            <h4 className="text-sm font-semibold text-blue-900">Takipçi Sayısı</h4>
            <p className="text-xs text-blue-700">CrewAtSea üzerinde şirketinizi takip eden denizciler</p>
          </div>
        </div>
        <div className="text-right">
          <span className="text-3xl font-bold text-blue-900">
            {company?.followerCount || company?.followersCount || company?._count?.followers || 0}
          </span>
          <span className="text-xs text-blue-600 block">denizci</span>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Logo & Banner */}
        <div className="bg-white rounded-maritime border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('profile.logoBanner.title')}</h3>
          
          <div className="space-y-6">
            {/* Logo Upload */}
            <div>
              <label className="label">{t('profile.logoBanner.logo')}</label>
              <div className="flex items-start gap-4">
                <div
                  className="w-32 h-32 rounded-maritime bg-gray-50 flex items-center justify-center border-2 border-dashed border-gray-300 hover:border-maritime-400 transition-colors cursor-pointer relative overflow-hidden"
                  onClick={() => logoInputRef.current?.click()}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={handleLogoDrop}
                >
                  {logoUrl ? (
                    <img src={logoUrl} alt="Logo" className="w-full h-full object-cover" />
                  ) : (
                    <div className="text-center">
                      <Building2 className="w-10 h-10 text-gray-400 mx-auto mb-1" />
                      <span className="text-xs text-gray-500">{t('profile.logoBanner.dragOrClick')}</span>
                    </div>
                  )}
                  
                  {/* Upload Progress Overlay */}
                  {uploadLogoMutation.isPending && (
                    <div className="absolute inset-0 bg-white/90 flex flex-col items-center justify-center">
                      <Loader2 className="w-6 h-6 animate-spin text-maritime-600 mb-2" />
                      <div className="w-20 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-maritime-600 transition-all duration-300"
                          style={{ width: `${logoProgress}%` }}
                        />
                      </div>
                      <span className="text-xs text-gray-600 mt-1">%{logoProgress}</span>
                    </div>
                  )}
                  
                  {/* Success Indicator */}
                  {uploadLogoMutation.isSuccess && !uploadLogoMutation.isPending && (
                    <div className="absolute top-1 right-1 bg-green-500 rounded-full p-0.5">
                      <CheckCircle2 className="w-4 h-4 text-white" />
                    </div>
                  )}
                </div>

                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => e.target.files?.[0] && handleLogoUpload(e.target.files[0])}
                />

                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => logoInputRef.current?.click()}
                      disabled={uploadLogoMutation.isPending}
                    >
                      <Upload className="w-4 h-4" />
                      {t('profile.logoBanner.uploadLogo')}
                    </button>
                    {company?.logoUrl && (
                      <button
                        type="button"
                        className="btn-secondary text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                        onClick={handleDeleteLogo}
                        disabled={deleteLogoMutation.isPending}
                      >
                        <Trash2 className="w-4 h-4" />
                        Sil
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-gray-500">
                    PNG, JPG {t('profile.logoBanner.or')} WEBP. Max 5MB.<br />
                    {t('profile.logoBanner.recommendedLogo')}
                  </p>
                </div>
              </div>
            </div>

            {/* Banner Upload */}
            <div>
              <label className="label">{t('profile.logoBanner.banner')}</label>
              <div
                className="h-40 rounded-maritime bg-gray-50 flex items-center justify-center border-2 border-dashed border-gray-300 hover:border-maritime-400 transition-colors cursor-pointer relative overflow-hidden"
                onClick={() => bannerInputRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleBannerDrop}
              >
                {bannerUrl ? (
                  <img src={bannerUrl} alt="Banner" className="w-full h-full object-cover" />
                ) : (
                  <div className="text-center">
                    <Image className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                    <span className="text-sm text-gray-500">{t('profile.logoBanner.bannerDragOrClick')}</span>
                    <p className="text-xs text-gray-400 mt-1">{t('profile.logoBanner.recommendedBanner')}</p>
                  </div>
                )}

                {/* Upload Progress Overlay */}
                {uploadBannerMutation.isPending && (
                  <div className="absolute inset-0 bg-white/90 flex flex-col items-center justify-center">
                    <Loader2 className="w-8 h-8 animate-spin text-maritime-600 mb-2" />
                    <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-maritime-600 transition-all duration-300"
                        style={{ width: `${bannerProgress}%` }}
                      />
                    </div>
                    <span className="text-sm text-gray-600 mt-1">%{bannerProgress}</span>
                  </div>
                )}

                {/* Success Indicator */}
                {uploadBannerMutation.isSuccess && !uploadBannerMutation.isPending && (
                  <div className="absolute top-2 right-2 bg-green-500 rounded-full p-1">
                    <CheckCircle2 className="w-5 h-5 text-white" />
                  </div>
                )}
              </div>

              <input
                ref={bannerInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => e.target.files?.[0] && handleBannerUpload(e.target.files[0])}
              />
              
              <div className="flex flex-wrap items-center gap-2 mt-3">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => bannerInputRef.current?.click()}
                  disabled={uploadBannerMutation.isPending}
                >
                  <Upload className="w-4 h-4" />
                  {t('profile.logoBanner.uploadBanner')}
                </button>
                {company?.bannerUrl && (
                  <button
                    type="button"
                    className="btn-secondary text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                    onClick={handleDeleteBanner}
                    disabled={deleteBannerMutation.isPending}
                  >
                    <Trash2 className="w-4 h-4" />
                    Sil
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Basic Info */}
        <div className="bg-white rounded-maritime border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('profile.basicInfo.title')}</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="label">{t('profile.basicInfo.companyName')} *</label>
              <input
                type="text"
                className={`input ${errors.name ? 'input-error' : ''}`}
                {...register('name')}
              />
              {errors.name && (
                <p className="mt-1 text-xs text-red-500">{errors.name.message}</p>
              )}
            </div>

            <div className="md:col-span-2">
              <label className="label">{t('profile.basicInfo.description')}</label>
              <textarea
                className="input min-h-[120px]"
                placeholder={t('profile.basicInfo.descriptionPlaceholder')}
                {...register('description')}
              />
            </div>

            <div>
              <label className="label">{t('profile.basicInfo.country')} *</label>
              <input
                type="text"
                className={`input ${errors.country ? 'input-error' : ''}`}
                {...register('country')}
              />
            </div>

            <div>
              <label className="label">{t('profile.basicInfo.city')}</label>
              <input
                type="text"
                className="input"
                {...register('city')}
              />
            </div>

            <div className="md:col-span-2">
              <label className="label">{t('profile.basicInfo.address')}</label>
              <input
                type="text"
                className="input"
                {...register('address')}
              />
            </div>
          </div>
        </div>

        {/* Contact Info */}
        <div className="bg-white rounded-maritime border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('profile.contact.title')}</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="label">{t('profile.contact.website')}</label>
              <input
                type="url"
                className={`input ${errors.website ? 'input-error' : ''}`}
                placeholder="https://www.example.com"
                {...register('website')}
              />
              {errors.website && (
                <p className="mt-1 text-xs text-red-500">{errors.website.message}</p>
              )}
            </div>

            <div>
              <label className="label">{t('profile.contact.email')}</label>
              <input
                type="email"
                className={`input ${errors.contactEmail ? 'input-error' : ''}`}
                placeholder="hr@example.com"
                {...register('contactEmail')}
              />
            </div>

            <div>
              <label className="label">{t('profile.contact.phone')}</label>
              <input
                type="tel"
                className="input"
                placeholder="+90 212 123 4567"
                {...register('contactPhone')}
              />
            </div>
          </div>
        </div>

        {/* Company Details */}
        <div className="bg-white rounded-maritime border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('profile.details.title')}</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="label">{t('profile.details.foundedYear')}</label>
              <input
                type="number"
                className="input"
                placeholder="1990"
                {...register('foundedYear', { valueAsNumber: true })}
              />
            </div>

            <div>
              <label className="label">{t('profile.details.employeeCount')}</label>
              <select className="input" {...register('employeeCount')}>
                <option value="">{t('profile.details.select')}</option>
                <option value="1-10">1-10</option>
                <option value="11-50">11-50</option>
                <option value="51-200">51-200</option>
                <option value="201-500">201-500</option>
                <option value="501-1000">501-1000</option>
                <option value="1000+">1000+</option>
              </select>
            </div>

            <div>
              <label className="label">{t('profile.details.fleetSize')}</label>
              <input
                type="number"
                className="input"
                placeholder="25"
                {...register('fleetSize', { valueAsNumber: true })}
              />
            </div>
          </div>

          {/* Vessel Types */}
          <div className="mt-6">
            <label className="label">{t('profile.details.vesselTypes')}</label>
            <p className="text-xs text-gray-500 mb-3">{t('profile.details.vesselTypesHint')}</p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(vesselTypeLabels).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => toggleVesselType(key)}
                  className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                    selectedVesselTypes.includes(key)
                      ? 'bg-maritime-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => {
              reset();
              setSelectedVesselTypes(originalVesselTypes);
              setVesselTypesChanged(false);
            }}
            disabled={!hasChanges}
          >
            {t('profile.actions.cancel')}
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={!hasChanges || updateMutation.isPending}
          >
            {updateMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {t('profile.actions.saving')}
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                {t('profile.actions.save')}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
